import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {combineLatest, EMPTY, forkJoin, from, mergeMap, Observable, of, throwError} from 'rxjs';
import {retryWithBackoff} from "../../libs/rxjs/retryWithBackoff";
import {catchError, delay, finalize, map, switchMap, take} from "rxjs/operators";
import {ADMINISTRATOR, appData, compareIds, concatIdBusinessId, DEFAULT_PROTOCOL, DEFAULT_SERVER, getCredentialsHeader, getCredentialsHeaderImage, getDefaultStationStats, isBase64, isGalaxy, isHostLocal, isNullOrUndef, isNullOrUndefOrEmpty, jsonToBase64, NONE_VALUE, preProcessSceneTree, SERVERS, SUPPORTED_FILE_TYPES} from "../common";
import {
    BusinessId,
    EnumStudioType,
    IAccountData,
    IAccountReplyV2,
    IAccountRequest,
    IAnnualPayData,
    IAppSettingsWrap,
    IAppStatic,
    IBusinessDomainCollection,
    IBusinessUsage,
    ICart,
    ICartCreate,
    ICartProduct,
    ICartReply,
    ICartRequest,
    ICatalogItemCategories,
    ICatalogItemInfos,
    ICatalogItemResources,
    ICatalogItems,
    ICategoryValues,
    ICRUDRecord,
    ICRUDRecordRead,
    ICRUDRecordReadReply,
    ICRUDRecordReply,
    Id,
    IDynawebsCustomer,
    IDynawebsServerReply,
    IEmailSend,
    IEventBlock,
    IEventParam,
    IGlobalDataRequest,
    IGlobalSettings,
    IMusicChannel,
    IMusicChannelSong,
    IOrderDetails,
    IOrderForm,
    IPaintSource,
    IPlayerProps,
    IPreferencesBusiness,
    IPreloadResellerInfo,
    IResource,
    IResourceSharedItem,
    ISceneTemplateImport,
    ISchedule,
    IScheduleEvent,
    ISharedResourceLibrary,
    ISocialReply,
    ISocialToken,
    IStation,
    IStationData,
    IStationGeoIp,
    IStationLogRequest,
    IStationStatsV1,
    IStationStatsV1V2,
    ITransitionPool,
    ITransitionPoolItem,
    IUser,
    IUserSaveDetailsReply,
    IUserSync,
    IUserUpgradeReply
} from "../interfaces";
import {StoreSelectorsService} from "./store-selectors.service";
import {actionAppSettings, actionStationV1V2Update, actionTwoFactorCheckAccess} from "../store/actions/user.actions";
import {ToastrService} from "ngx-toastr";
import {SocialUser} from "@abacritt/angularx-social-login";
import {ISocialServerReply} from "../interfaces/social-interface";
import {TranslocoService} from "@ngneat/transloco";
import {IAccountDetails} from "../interfaces/enterprise-account-interface";
import {Compbaser} from "../components/compbaser";
import {LocalStoreService} from './local-store.service';
import {staticAccountTemplates} from "../../assets/accountTemplates/acounts-constants";
import {ISceneImportTemplate, sceneTemplates} from "../../assets/accountTemplates/scene-template-constants";
import {SelectionModel} from "@angular/cdk/collections";
import io, {Socket} from "socket.io-client";
import {IChatServerReply, IChatStats} from "../interfaces/chat-interface";
import {Lib} from "../../libs/Lib";
import * as xml2js from 'xml2js';
import * as _ from "lodash";
import {Action} from '@ngrx/store/src/models';
import {FintechService} from "./fintech.service";

/**
 -- note: also checkout latest XML parser at: https://github.com/michaelkourlas/node-js2xmlparser

 X2JS used version: https://github.com/Leonidas-from-XIV/node-xml2js
 to make the project work in Angular we need to provide our sax, timer and streams lib: https://github.com/angular/angular-cli/issues/10694
 "sax": "^1.2.4",
 "stream": "0.0.2",
 "timers": "^0.1.1",

 and add to tsconfig.js the following config:

 "paths": {
 "sax": [
 "node_modules/sax"
 ],
 "timers": [
 "node_modules/timers"
 ],
 "stream": [
 "node_modules/stream"
 ]
 },
 you can also checkout https://github.com/mboughaba/browser-xml2js (which I am NOT using) which converted X2JS to a browser version
 **/

declare const $: any;

const httpOptionsJson = {
    headers: new HttpHeaders({'Content-Type': 'application/json'}),
    responseType: 'json' as 'json'
};

@Injectable()
export class UserService extends Compbaser {
    private m_user: IUser;
    private appSettings: IAppStatic;
    private userMask: Array<string>;
    private isFullFeatures: boolean;
    private studioType: EnumStudioType;
    private coreAppData: IAppSettingsWrap;
    private socketChat: Socket;

    constructor(
        private translocoService: TranslocoService,
        private _http: HttpClient,
        private localStoreService: LocalStoreService,
        private storeSelector: StoreSelectorsService,
        private toast: ToastrService,
        private fintechService: FintechService) {

        super();

        this.storeSelector.dispatch(actionAppSettings({appData}));
        this.cod(
            this.storeSelector.isFullFeatures().subscribe((v) => {
                this.isFullFeatures = v;
            })
        );
        this.cod(
            this.storeSelector.getCoreData()
                .subscribe((i_coreData: IAppSettingsWrap) => {
                    this.coreAppData = i_coreData;
                })
        );
        this.cod(
            this.storeSelector.getUserAccessMask()
                .subscribe((value) => {
                    this.userMask = value;
                })
        );
        this.cod(
            storeSelector.getUser()
                .subscribe((user: IUser) => {
                    this.m_user = user;
                    this.studioType = user.studioType;
                })
        );
        this.cod(
            storeSelector.getAppSettings()
                .subscribe((appSettings: IAppStatic) => {
                    this.appSettings = appSettings;
                })
        );
    }

    // private getCredentialsHeader(): object {
    //     const credentials = Lib.EncryptUserPass(this.user.username, this.user.passwordNew);
    //     return {headers: new HttpHeaders({'Content-Type': 'application/json', 'Authorization': credentials}), responseType: 'json' as 'json'};
    // }

    private onError(i_msg) {
        console.log('error ' + i_msg);
        this.toast.error(i_msg);
    }

    public getSharedResourcesLibrary(businessDomains: IBusinessDomainCollection): Observable<ISharedResourceLibrary> {
        const observables: Array<Observable<any>> = [];
        // tslint:disable-next-line:forin
        for (const source in businessDomains) {
            // const i_businesses = config[source];
            // if (source.indexOf('mars') > -1 || source.indexOf('mercury') > -1) continue;
            const url = `https://${source}/WebService/SharedDataService.asmx/GetSharedResources?i_search=`;
            observables.push(this._http.get(url, {
                headers: new HttpHeaders({
                    'Content-Type': 'text/xml',
                    'Accept': 'text/xml'
                }), responseType: 'text' as 'json'
            })
                .pipe(
                    retryWithBackoff(url, this.storeSelector, this.toast),
                    map(xml => ({xml, source})),
                    catchError((e) => {
                        console.error('problem loading users ' + e + ' ' + source + ' ' + e.toString());
                        this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                        return e;
                    })
                ));
        }
        return forkJoin(observables).pipe(
            map((val) => {
                const resourcesLibrary: Array<IResourceSharedItem> = [];
                val.forEach((config) => {
                    const xmlData = config.xml.replace(/&lt;/ig, '<').replace(/&gt;/ig, '>');
                    xml2js.parseString(xmlData, (err, result) => {
                        if (err) {
                            console.error('getBusinessDomainCollection xml conversion error');
                            this.storeSelector.sendEmail(ADMINISTRATOR, 'error in painter json', this?.m_user?.email, true);
                            return this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                        }
                        const sharedResources: Array<IResourceSharedItem> = result.Result.Resource;
                        sharedResources.forEach((resource) => {
                            if (resource.$.treePath === '') return;
                            resource.url = `${this.appSettings.cloudResourcesS3}/business${resource.$.businessId}/resources/${resource.$.resourceId}.${resource.$.resourceType}`;
                            resourcesLibrary.push(resource);
                        });
                    });
                });
                return {
                    version: '0',
                    library: resourcesLibrary
                };
            }));
    }

    // testSaveDataUpdate() {
    //     const body = {player_data_value: "Update3"};
    //     this.CRUDRecord('player_data', 'player_data_id', 'update', body, 226);
    // }

    // testSaveDataDelete() {
    // this.CRUDRecord('player_data', 'player_data_id', 'delete', null, 243);
    // }

    // testSaveDataFileSelected(event) {
    //     const file: File = event.target.files[0];
    //     if (file) {
    //         const formData = new FormData();
    //         formData.append("thumbnail", file);
    //         // this.CRUDRecord('resources', 'resource_id', 'upload', formData, null);
    //     }
    // }

    // testSaveDataAdd() {
    //     const body = {
    //         player_data_value: "<Player player=\"3510\" label=\"Label1\" mimeType=\"mainPlayer\"><Metadata><Theme id=\"1000\" /></Metadata><Data><Layout rotation=\"0\" x=\"0\" y=\"0\" width=\"300\" height=\"300\" /><Background color=\"13408767\" alpha=\"1\" /><Scene defaultDuration=\"10\" scrolling=\"0\"><Layout><BasicLayout /></Layout><Players><Player player=\"3241\" label=\"item3\"><AnchorLayoutData left=\"NaN\" horizontalCenter=\"NaN\" right=\"10\" top=\"NaN\" verticalCenter=\"-10\" bottom=\"NaN\" /><Data><Layout rotation=\"0\" x=\"146\" y=\"-1.4210854715202004e-14\" width=\"154\" height=\"100\" /><Background color=\"3381759\" alpha=\"1\" /><Label><Text>Item3</Text><Font fontSize=\"36\" fontColor=\"13382400\" fontFamily=\"Bearpaw\" fontWeight=\"normal\" fontStyle=\"normal\" textDecoration=\"none\" textAlign=\"left\" autoScaling=\"0\" /></Label></Data></Player><Player player=\"3241\" label=\"item4\" autoScaling=\"1\"><AnchorLayoutData left=\"NaN\" horizontalCenter=\"NaN\" right=\"10\" top=\"NaN\" verticalCenter=\"-10\" bottom=\"NaN\" /><Data><Layout rotation=\"0\" x=\"146\" y=\"100\" width=\"154\" height=\"100\" /><Background color=\"13395660\" alpha=\"1\" /><Label><Text>Item4</Text><Font fontSize=\"36\" fontColor=\"16776960\" fontFamily=\"Bearpaw\" fontWeight=\"normal\" fontStyle=\"normal\" textDecoration=\"none\" textAlign=\"left\" autoScaling=\"0\" /></Label></Data></Player><Player player=\"3241\" label=\"item1\"><AnchorLayoutData left=\"10\" horizontalCenter=\"NaN\" right=\"NaN\" top=\"NaN\" verticalCenter=\"10\" bottom=\"NaN\" /><Data><Layout rotation=\"0\" x=\"0\" y=\"-1.4210854715202004e-14\" width=\"146\" height=\"100\" /><Background color=\"26316\" alpha=\"1\" /><Label><Text>Item1;`a2`</Text><Font fontSize=\"14\" fontColor=\"65280\" fontFamily=\"Arial\" fontWeight=\"normal\" fontStyle=\"normal\" textDecoration=\"none\" textAlign=\"left\" autoScaling=\"0\" /></Label></Data></Player><Player player=\"3241\" label=\"item2\" autoScaling=\"1\"><AnchorLayoutData left=\"10\" horizontalCenter=\"NaN\" right=\"NaN\" top=\"NaN\" verticalCenter=\"10\" bottom=\"NaN\" /><Data><Layout rotation=\"0\" x=\"0\" y=\"100\" width=\"146\" height=\"100\" /><Background color=\"65484\" alpha=\"1\" /><Label><Text>Item2</Text><Font fontSize=\"36\" fontColor=\"13395711\" fontFamily=\"Bearpaw\" fontWeight=\"normal\" fontStyle=\"normal\" textDecoration=\"none\" textAlign=\"left\" autoScaling=\"0\" /></Label></Data></Player></Players></Scene></Data></Player>"
    //     };
    // }

    // todo: add compression gzip support return this._http.post(url, JSON.stringify(crudRecord.body), {headers: {'Content-Encoding': 'gzip'}}), see: https://betterexplained.com/articles/how-to-optimize-your-site-with-gzip-compression/
    crudRecord(crudRecord: ICRUDRecord): Observable<ICRUDRecordReply> {
        let url: string;
        let user = this?.m_user?.email;
        let password = this?.m_user?.passwordNew;
        let webApiBaseUrl = this?.m_user?.webApiBaseUrl;
        if (crudRecord.overrideCredentials) {
            user = crudRecord.overrideCredentials.user;
            password = crudRecord.overrideCredentials.password;
            webApiBaseUrl = crudRecord.overrideCredentials.webApiBaseUrl;
        }
        url = `${webApiBaseUrl}/Web/saveData.ashx?user=${user}&password=${Lib.Base64().encode(password)}&table=${crudRecord.table}&primary=${crudRecord.primary}&studioId=${crudRecord.studioId}&command=${crudRecord.command}&base64=1`;
        if (crudRecord.id != null) {
            url += crudRecord.command === 'add' ? `&id=${crudRecord.id}` : `&ids=[${crudRecord.id}]`;
        }
        if (crudRecord.ext != null) url += `&name=${crudRecord.name}&ext=${crudRecord.ext}`;
        const restartPlayer = this.localStoreService.getStorageValConfig('restartPlayer') == '1' ? true : false;
        /** replay is only for PWA Signage Players and a scene is saved, if assigned to player, it will restart it **/
        url += `&replay=${restartPlayer}`;

        // switch (crudRecord.command) {
        //     case 'delete': {
        //         if (crudRecord.id !== null) url += `&ids=[${crudRecord.id}]`;
        //         break;
        //     }
        //     default: {
        //         if (crudRecord.id !== null) url += `&ids=[${crudRecord.id}]`;
        //         break;
        //     }
        // }

        const noStringify = crudRecord?.options?.noStringify;
        let opt = {};
        if (crudRecord?.options?.reportProgress) {
            opt = {reportProgress: true, observe: 'events', headers: new HttpHeaders({'ngsw-bypass': ''})};
        }
        return this._http.post(url,
            noStringify ? crudRecord.body : JSON.stringify(crudRecord.body), opt)
            .pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((err) => {
                    this.showSaveError(err, crudRecord.command);
                    return throwError(err);
                }),
                finalize(() => {
                }),
                map((result: ICRUDRecordReply) => {
                    if (opt['reportProgress'] && result && result.hasOwnProperty('type') && result.hasOwnProperty('status') == false) {
                        return result;
                    } else if (!result?.status) {
                        this.showSaveError(result, crudRecord.command);
                        return {id: null, status: false};
                    }
                    return result;
                })
            );
    }

    // fileUpload(crudRecord: ICRUDRecord, callback = null) {
    //     const formData = crudRecord.body;
    //     const xhr = new XMLHttpRequest();

    //     let url = `${this.m_user.webApiBaseUrl}/Web/saveData.ashx?user=${this.m_user.email}&password=${this.m_user.passwordNew}&table=${crudRecord.table}&primary=${crudRecord.primary}&studioId=${crudRecord.studioId}&command=${crudRecord.command}`;
    //     xhr.open("POST", url);

    //     xhr.upload.addEventListener("loadstart", (evt) => {
    //         console.log("upload starting...\n");
    //     }, false);

    //     xhr.upload.addEventListener("progress", (evt) => {
    //         var percentLoaded = (evt.loaded / evt.total) * 100;
    //         console.log(evt.loaded + "% loaded\n");
    //         console.log(percentLoaded + "%\n");
    //         if (callback) callback(percentLoaded.toFixed(2));
    //     }, false);

    //     xhr.upload.addEventListener("load", (evt) => {
    //         console.log("upload complete!");
    //         if (callback) callback(100);
    //     }, false);

    //     xhr.send(formData);
    // }

    // todo: add compression gzip support return this._http.post(url, JSON.stringify(crudRecord.body), {headers: {'Content-Encoding': 'gzip'}}), see: https://betterexplained.com/articles/how-to-optimize-your-site-with-gzip-compression/
    // crudRecordRead(crudRecord: ICRUDRecordRead): Observable<ICRUDRecordReadReply> {
    crudRecordReadCall(crudRecord: ICRUDRecordRead): Observable<ICRUDRecordReadReply> {
        //   const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=GetAccountStatus&businessId=${businessId}`;
        //   return this._http.jsonp<any[]>(url, 'callback').pipe(
        const getDomainUrl = `${DEFAULT_PROTOCOL}${DEFAULT_SERVER}/WebService/getBusinessDomain.ashx?businessId=${crudRecord.businessId}`;
        return this._http.jsonp(getDomainUrl, 'callback')
            .pipe(
                retryWithBackoff(getDomainUrl, this.storeSelector, this.toast),
                catchError((err) => {
                    this.showSaveError(err, 'crudRecordReadCall 1');
                    return throwError(err);
                }),
                map((v: any) => {
                    return `${DEFAULT_PROTOCOL}${v.domain}/Web/loadCachedData.ashx?id=${crudRecord.id}&changelist=${crudRecord.changelist}&businessId=${crudRecord.businessId}&table=${crudRecord.table}&reqFor=${crudRecord.reqFor}`;
                }),
                mergeMap((u: any) => {
                    return this._http.post(u, {});
                }))
            .pipe(
                retryWithBackoff('loadCachedData...', this.storeSelector, this.toast),
                catchError((err) => {
                    this.showSaveError(err, 'crudRecordReadCall 2');
                    return throwError(err);
                }), map((i_serveReply: any) => {
                    const record: ICRUDRecordReadReply = {
                        player_data_value: i_serveReply?.player_data_value,
                        resource_bytes_total: i_serveReply?.resource_bytes_total,
                        resource_name: i_serveReply?.resource_name,
                        resource_type: i_serveReply?.resource_type
                    };
                    return record;
                }));
    }

    getProxyResource(i_value, type: 'NODE' | 'IIS'): Observable<any> {
        /** example redirect proxy service
         iis:
         const url = `${this.coreAppData[0].webApiBaseUrl}/Web/redirectRequest.ashx?url=${this.coreAppData[1].cloudResourcesS3}/business${this.coreAppData[0].businessId}/resources/${i_resource.resource_id}.${i_resource.resource_type}`;
         const url = 'https://earth.signage.me/Web/redirectRequest.ashx?url=https://s3-us-west-2.amazonaws.com/oregon-signage-resources/business391060/resources/405.jpg';

         node:
         svgPath = Lib.Base64().encode(svgPath) or atob(svgPath);
         let svgPath = `${this.this.coreAppData[1].protocol}${this.this.coreAppData[0].businessDomain}/Resources/business${this.this.coreAppData[0].businessId}/resources/${item_resource.resource_id}.svg`;
         `${i_this.coreAppData[1].globalService}/proxyRequest/${svgPath}`
         **/
        let url;
        switch (type) {
            case "NODE": {
                const svgPath = atob(i_value);
                url = `${this.coreAppData[1].globalService}/proxyRequest/${svgPath}`;
                break;
            }
            case "IIS": {
                url = `${this.coreAppData[0].webApiBaseUrl}/Web/redirectRequest.ashx?url=${this.coreAppData[1].cloudResourcesS3}/business${this.coreAppData[0].businessId}/resources/${i_value.resource_id}.${i_value.resource_type}`;
                break;
            }
        }
        const opt = {};
        return this._http.post(url, {url: i_value}, opt)
            .pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((err) => {
                    console.error('getProxyResource error command ' + err);
                    this.onError(this.translocoService.translate('toast.sorryProxyProblem', {}, this.translocoService.getActiveLang()));
                    return throwError(err);
                }),
                finalize(() => {
                }),
                map((result: any) => {
                    return result;
                })
            );
    }

    getUserScenesData(businessId: BusinessId, i_changelistId): Observable<IAccountData> {
        let url: string;
        this.appSettings.workOffline ? url = this.appSettings.apiOfflineUrl + 'data.json' : url = `${this.m_user.webApiBaseUrl}/Web/loadData.ashx?businessId=${this.m_user.businessId}&userId=${Lib.Base64().encode(this.m_user.username)}&fromChangelistId=${i_changelistId}`;
        let globalSettings: Array<IGlobalSettings>;
        let preferences: IPreferencesBusiness = {};
        let scenes: Array<IPlayerProps>;
        let resources: Array<IResource>;
        const stations: Array<IStation> = [];
        const liveStations: Array<IStation> = [];
        const stationsV1Stats: Array<IStationStatsV1> = [];
        const stationsV2Stats: Array<IStationStatsV1V2> = [];
        let transitionPools: Array<ITransitionPool>;
        let transitionPoolItems: Array<ITransitionPoolItem>;
        let musicChannels: Array<IMusicChannel>;
        let musicChannelSongs: Array<IMusicChannelSong>;
        let catalogItems: Array<ICatalogItems>;
        let catalogItemInfos: Array<ICatalogItemInfos>;
        let catalogItemResources: Array<ICatalogItemResources>;
        let categoryValues: Array<ICategoryValues>;
        let catalogItemCategories: Array<ICatalogItemCategories>;
        let eventParams: Array<IEventParam>;
        let eventsStrings: Array<string> = [];
        let eventBlocks: Array<IEventBlock> = [];
        let schedules: Array<ISchedule>;
        let scheduleEvents: Array<IScheduleEvent>;
        let painters: Array<IPaintSource>;

        Lib.DevShow('scenes are loading ' + url);
        return this._http.get<IPlayerProps[]>(url).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((playerData: IAccountData) => {
                if (isNullOrUndef(playerData) || playerData.status === false) {
                    this.toast.error(this.translocoService.translate('toast.problemLoadingAccount', {}, this.translocoService.getActiveLang()), playerData?.msg);
                    console.error(playerData?.msg);
                    if (playerData['msg'] && Lib.DevMode()) {
                        console.log(`Server Error user load: ${playerData['msg']}`);
                    }
                    const accountDataNoData: IAccountData = {
                        changelistId: NONE_VALUE,
                        globalSettings, resources, scenes, stations, liveStations, catalogItemInfos, catalogItemResources,
                        catalogItems, categoryValues, eventParams, catalogItemCategories,
                        transitionPools, transitionPoolItems, musicChannels, musicChannelSongs,
                        schedules, scheduleEvents, eventsStrings, eventBlocks, painters
                    };
                    return accountDataNoData;
                }
                if (playerData.globalSettings) {
                    globalSettings = [];
                    Object.keys(playerData.globalSettings).forEach((key: string) => {
                        if (playerData.globalSettings[key].change_type === 3 && i_changelistId === NONE_VALUE || key == '-1') return;
                        globalSettings.push({
                            ...playerData.globalSettings[key],
                            param_id: key,
                            // enabled: 'true'
                        });
                    });
                }
                if (playerData.preferences) {
                    preferences = Lib.PreferencesProcess(playerData.preferences);
                    if (preferences && preferences?.twoFactorState?.value?.token?.length > 10) {
                        this.storeSelector.dispatch(actionTwoFactorCheckAccess({payload: preferences.twoFactorState, status: false}));
                    }
                }
                if (playerData.musicChannels) {
                    musicChannels = [];
                    Object.keys(playerData.musicChannels).forEach((key: string) => {
                        if (playerData.musicChannels[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.musicChannels[key]['music_channel_id'] = String(key);
                        playerData.musicChannels[key]['folder'] = playerData.musicChannels[key]?.tree_path;
                        musicChannels.push(playerData.musicChannels[key]);
                    });
                }
                if (playerData.musicChannelSongs) {
                    musicChannelSongs = [];
                    Object.keys(playerData.musicChannelSongs).forEach((key: string) => {
                        if (playerData.musicChannelSongs[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.musicChannelSongs[key]['music_channel_song_id'] = String(key);
                        musicChannelSongs.push(playerData.musicChannelSongs[key]);
                    });
                }
                if (playerData.transitionPools) {
                    transitionPools = [];
                    Object.keys(playerData.transitionPools).forEach((key: string) => {
                        if (playerData.transitionPools[key].change_type == 3 && i_changelistId === NONE_VALUE) return;
                        playerData.transitionPools[key]['id'] = String(key);
                        playerData.transitionPools[key]['handle'] = key;
                        transitionPools.push(playerData.transitionPools[key]);
                    });
                }
                if (playerData.transitionPoolItems) {
                    transitionPoolItems = [];
                    Object.keys(playerData.transitionPoolItems).forEach((key: string) => {
                        const poolItem = playerData.transitionPoolItems[key];
                        const cleanedData = poolItem.transition_item_data.replace("\ufeff", "");
                        xml2js.parseString(cleanedData, (err, transitionData) => {
                            if (err) {
                                this.storeSelector.sendEmail(ADMINISTRATOR, 'error in transition json', this?.m_user?.email, true);
                                return;
                            }
                            if (poolItem.change_type == 3 && i_changelistId === NONE_VALUE) return;
                            transitionPoolItems.push({
                                transition_pool_item_id: key,
                                transition_pool_id: String(poolItem.transition_pool_id),
                                transition_item_name: poolItem.transition_item_name,
                                transition_item_data: transitionData
                            });
                        });
                    });
                }
                if (playerData.eventParams) {
                    eventParams = [];
                    Object.keys(playerData.eventParams).forEach((key: string) => {
                        if (playerData.eventParams[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.eventParams[key]['event_param_id'] = key;
                        try {
                            playerData.eventParams[key]['event_value'] = JSON.parse(playerData.eventParams[key].event_value);
                        } catch (e) {
                            console.error('skipping event_value due to json error, event id: ' + key + ' error: ' + e);
                            return;
                        }
                        eventParams.push(playerData.eventParams[key]);
                    });
                }
                if (playerData.resources && _.size(playerData.resources) > 0) {
                    resources = [];
                    Object.keys(playerData.resources).forEach((key: string) => {
                        if (SUPPORTED_FILE_TYPES.indexOf(playerData.resources[key].resource_type.toLowerCase()) === -1) return;
                        if (this.userMask && this.userMask?.findIndex((v) => compareIds(v, playerData.resources[key].access_key)) === -1) return;
                        if (playerData.resources[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        if (playerData.resources[key]['default_player'] === '') {
                            const type = playerData.resources[key]['resource_type'].toLowerCase();
                            playerData.resources[key]['default_player'] = this.storeSelector.getBlockLabelByFileType(type).id;
                        }
                        resources.push(playerData.resources[key]);
                    });
                }

                if (playerData.branchStations) {
                    Object.keys(playerData.branchStations).forEach((key: string) => {
                        if (playerData.branchStations[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        const _station = playerData.branchStations[key];
                        _station['station_id'] = key;
                        _station['version'] = 1;
                        _station['station_business_id'] = concatIdBusinessId(key, businessId, 1);
                        _station['player_data_id'] = String(_station['player_data_id']);
                        _station['tree_path'] = _station['tree_path'];
                        stations.push(_station);
                    });
                }

                if (playerData.stations) {
                    Object.keys(playerData.stations).forEach((key: string) => {
                        if (playerData.stations[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        playerData.stations[key]['station_id'] = key;
                        playerData.stations[key]['version'] = 2;
                        playerData.stations[key]['station_business_id'] = concatIdBusinessId(key, businessId, '2');
                        playerData.stations[key]['tree_path'] = playerData.stations[key]['tree_path'];
                        playerData.stations[key]['player_data_id'] = String(playerData.stations[key]['player_data_id']);
                        stations.push(playerData.stations[key]);
                        stationsV2Stats.push(getDefaultStationStats(concatIdBusinessId(key, businessId, '2'), key, '2'));
                    });
                    // for v2 stations we create a default station stats until later when we get real station stats from wss socket
                    this.storeSelector.dispatch(actionStationV1V2Update({stationArray: stationsV2Stats}));
                }

                if (playerData.categoryValues) {
                    categoryValues = [];
                    Object.keys(playerData.categoryValues).forEach((key: string) => {
                        if (playerData.categoryValues[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        const parentId = isNullOrUndef(playerData.categoryValues[key].parent_category_value_id) ? -1 : playerData.categoryValues[key].parent_category_value_id;
                        categoryValues.push({
                            category_values_id: key,
                            parent_category_value_id: parentId,
                            category_value: playerData.categoryValues[key].category_value,
                        });
                    });
                }
                if (playerData.catalogItemCategories) {
                    catalogItemCategories = [];
                    Object.keys(playerData.catalogItemCategories).forEach((key: string) => {
                        if (playerData.catalogItemCategories[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        catalogItemCategories.push({
                            catalog_item_category_id: key,
                            catalog_item_id: playerData.catalogItemCategories[key].catalog_item_id,
                            category_value_id: playerData.catalogItemCategories[key].category_value_id
                        });
                    });
                }
                if (playerData.catalogItemResources) {
                    catalogItemResources = [];
                    Object.keys(playerData.catalogItemResources).forEach((key: string) => {
                        if (playerData.catalogItemResources[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        catalogItemResources.push({
                            catalog_item_resource_id: key,
                            catalog_item_id: playerData.catalogItemResources[key].catalog_item_id,
                            resource_id: playerData.catalogItemResources[key].resource_id,
                        });
                    });
                }
                if (playerData.catalogItemInfos) {
                    catalogItemInfos = [];
                    Object.keys(playerData.catalogItemInfos).forEach((key: string) => {
                        if (playerData.catalogItemInfos[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        catalogItemInfos.push({
                            catalog_item_id: key,
                            info0: playerData.catalogItemInfos[key].info0,
                            info1: playerData.catalogItemInfos[key].info1,
                            info2: playerData.catalogItemInfos[key].info2,
                            info3: playerData.catalogItemInfos[key].info3,
                        });
                    });
                }
                if (playerData.catalogItems) {
                    catalogItems = [];
                    Object.keys(playerData.catalogItems).forEach((key: string) => {
                        if (playerData.catalogItems[key].change_type === 3 && i_changelistId === NONE_VALUE) return;
                        catalogItems.push({
                            catalog_item_id: key,
                            item_name: playerData.catalogItems[key].item_name
                        });
                    });
                }
                if (playerData.schedules) {
                    schedules = [];
                    Object.keys(playerData.schedules).forEach((key: string) => {
                        if (playerData.schedules[key].change_type === 3 && i_changelistId === NONE_VALUE || key == '-1') return;
                        schedules.push({
                            schedule_id: key,
                            schedule_name: playerData.schedules[key].schedule_name,
                            description: playerData.schedules[key].description
                        });
                    });
                }
                if (playerData.scheduleEvents) {
                    scheduleEvents = [];
                    Object.keys(playerData.scheduleEvents).forEach((key: string) => {
                        if (playerData.scheduleEvents[key].change_type === 3 && i_changelistId === NONE_VALUE || key == '-1') return;
                        scheduleEvents.push({
                            ...playerData.scheduleEvents[key],
                            schedule_event_id: key,
                            // enabled: 'true'
                        });
                    });
                }
                if (playerData.painters) {
                    painters = [];
                    Object.keys(playerData.painters).forEach((key: string) => {
                        // Lib.DevShow('processing paint id: ' + key);
                        if (playerData.painters[key].change_type === 3 && i_changelistId === NONE_VALUE || key == '-1') return;
                        // if (this.userMask && this.userMask?.findIndex((v) => compareIds(v, playerData.painters[key].access_key)) === -1) return;
                        let painterData = playerData.painters[key]['painter_data'];
                        // var startTime = performance.now()
                        if (isBase64(painterData)) {
                            painterData = atob(painterData);
                        }
                        if (painterData === 'undefined' || painterData === '' || painterData === 'null') {
                            return;
                        }
                        // var endTime = performance.now()
                        // console.log(`Call to doSomething took ${endTime - startTime} milliseconds`);
                        let backgroundData = playerData.painters[key]['background'];
                        try {
                            if (painterData) painterData = JSON.parse(painterData);
                        } catch (err) {
                            console.log('Corrupted painter found, paint id ' + key + ' ' + err.message);
                            if (Lib.DevMode()) {
                                alert('Corrupted painter found, paint id ' + key + ' ' + err.message);
                            } else {
                                this.storeSelector.sendEmail(ADMINISTRATOR, 'error in painter json', this?.m_user?.email, true);
                            }
                            return;
                        }
                        if (backgroundData) backgroundData = JSON.parse(backgroundData);
                        painters.push({
                            ...playerData.painters[key],
                            id: String(key),
                            painter_data: painterData,
                            background: backgroundData
                        });
                    });
                }
                if (playerData.scenes) {
                    scenes = [];
                    eventsStrings = [];
                    eventBlocks = [];
                    Object.keys(playerData.scenes).forEach((key: string, idx) => {
                        if (!playerData.scenes[key]) return;
                        if (this.userMask && this.userMask?.findIndex((v) => compareIds(v, playerData.scenes[key].access_key)) === -1) return;
                        const xmlData = playerData.scenes[key].player_data_value;
                        if (playerData.scenes[key]?.change_type === 3) return;
                        // Lib.DevShow('processing scene id: ' + key);
                        xml2js.parseString(xmlData, {charkey: '_'}, (err, sceneData: IPlayerProps) => {
                            if (err || !sceneData) {
                                console.log('Corrupted scene found, scene id ' + key + ' ' + err?.message);
                                if (Lib.DevMode()) {
                                    alert('Corrupted scene found scene id ' + key + ' ' + err?.message);
                                } else {
                                    /** only report errors for SignStudio as sometimes will get errors from components built in Pro, we just do not show them but not report issues **/
                                    if (this.studioType === EnumStudioType.SIGN_STUDIO) {
                                        this.storeSelector.sendEmail(ADMINISTRATOR, 'error in scene json', this?.m_user?.email, true);
                                    }
                                }
                                return;
                            }

                            // inject IDs to src and id
                            // preProcessScene(sceneData, key);
                            // eventBlocks.push(...preProcessScene(sceneData, key));
                            const eventNames = preProcessSceneTree(sceneData, key);
                            if (eventNames.length > 0) {
                                eventsStrings = [...eventsStrings, ...eventNames];
                                eventsStrings = [...new Set(eventsStrings)];
                            }

                            if (playerData.scenes[key]?.change_type === 3 && i_changelistId === NONE_VALUE) return;
                            if (!sceneData) {
                                Lib.DevShow('problem loading scene id: ' + key);
                                return;
                            }
                            sceneData.Player.$.folder = playerData.scenes[key].tree_path;
                            sceneData.Player.$.accessKey = playerData.scenes[key].access_key;
                            sceneData.Player.$.changelist_id = playerData.scenes[key].changelist_id;
                            sceneData.Player.$.private_child = playerData.scenes[key].private_child;
                            sceneData.Player.$.player_data_public = playerData.scenes[key]?.player_data_public;
                            scenes.push(sceneData);

                            /******************************************** dev mode debugging **************************************************/
                            // if (Lib.DevMode() && sceneData.Player.$.label.toLowerCase() === 'weather simple xx')
                            if (Lib.DevMode() && sceneData.Player.$.label.toLowerCase() === 'collection scene')
                                // if (Lib.DevMode() && sceneData.Player.$.label.toLowerCase() === '500px scene')
                                console.log('scene wip: ' + sceneData.Player.$.label + ' ' + idx);
                            /******************************************************************************************************************/
                        });
                    });
                }
                const accountData: IAccountData = {
                    changelistId: playerData.changelistId,
                    globalSettings,
                    preferences,
                    resources,
                    scenes,
                    stations,
                    liveStations,
                    catalogItemInfos,
                    catalogItemResources,
                    catalogItems,
                    categoryValues,
                    catalogItemCategories,
                    schedules,
                    scheduleEvents,
                    transitionPools,
                    transitionPoolItems,
                    musicChannels,
                    musicChannelSongs,
                    eventParams,
                    eventsStrings,
                    eventBlocks,
                    painters
                };
                return accountData;
            })
        );
    }

    getGlobalServiceData(i_endPoint: string, i_body: IGlobalDataRequest, i_useCredentials: boolean): Observable<Array<any>> {
        const headers = i_useCredentials ? getCredentialsHeader(this.m_user) : {headers: new HttpHeaders({'Content-Type': 'application/json'})};
        const content = JSON.stringify(i_body);
        const url = `${this.appSettings.globalService}/${i_endPoint}`;
        const httpCall = i_useCredentials ? this._http.put<any>(url, content, headers) : this._http.post(url, {
            headers,
            responseType: 'text' as 'json',
            content
        });
        return httpCall
            .pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((err) => {
                    console.error('Error loading end point ' + err);
                    this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                    // return Observable.of(true);
                    return throwError(err);
                }),
                finalize(() => {
                }),
                map((result: any) => {
                    return result;
                })
            );
    }

    getServerStationsGeoIPs(i_enterpriseMode: boolean): Observable<Array<IStationGeoIp>> {
        const stationsIps = [];
        let stationsGeoIps: Array<IStationGeoIp> = [];
        return this.storeSelector
            .getStationStats()
            .pipe(take(1),
                map((i_stations) => {
                    i_stations.forEach((i_station) => {
                        // if (i_station.$.connection == '1'){
                        //     alert(1);
                        // }
                        let ip = '';
                        if (!isNullOrUndefOrEmpty(i_station.$?.publicIp)) {
                            ip = i_station.$.publicIp;
                        } else if (!isNullOrUndefOrEmpty(i_station.$?.localIp)) {
                            ip = i_station.$.localIp;
                            // local ip 192.x.x.x
                            // } else if (!isNullOrUndefOrEmpty(i_station.$?.localAddress)) {
                            //     ip = i_station.$.localAddress;
                        } else if (!isNullOrUndefOrEmpty(i_station.$?.geoLocation?.ip)) {
                            ip = i_station.$.geoLocation.ip;
                        }
                        const geoLocation = i_station.$.geoLocation;
                        const id = i_station.$.id;
                        const businessId = i_station.$.businessId;
                        const source = i_station.$.source;
                        // only get stations with public ip and no location info
                        if (!isNullOrUndefOrEmpty(ip) && isNullOrUndefOrEmpty(geoLocation))
                            stationsIps.push({
                                id,
                                ip,
                                version: i_station.$.version,
                                source: this.m_user.sourceId,
                                businessId: isNullOrUndefOrEmpty(businessId) ? this.m_user.businessId : businessId,
                            });
                    });
                }),
                switchMap((v) => {
                    const content = JSON.stringify(stationsIps);
                    return this._http.post(`${this.appSettings.globalService}/getGeoByIp_v2`,
                        {
                            headers: new HttpHeaders({'Content-Type': 'application/json'}),
                            responseType: 'text' as 'json', content
                        }).pipe(
                        catchError((err) => {
                            console.error('Error loading station IPs geo location ' + err);
                            // this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                            // return Observable.of(true);
                            return throwError(err);
                        }),
                        finalize(() => {
                            // console.log('done!');
                        }),
                        map((result: any) => {
                            const stations = result;
                            // tslint:disable-next-line:forin
                            for (const station in stations) {
                                const i_station = stations[station];
                                const rand = _.random(0, 30) / 100;
                                i_station.station_business_id = concatIdBusinessId(stations[station].id, stations[station].businessId, stations[station].version);
                                i_station.lat = (i_station.lat + rand).toFixed(4);
                                i_station.lon = (i_station.lon + rand).toFixed(4);
                                i_station['city'] = i_station.city;
                                i_station['country'] = i_station.country;
                            }
                            return stationsGeoIps = stations;
                        })
                    );
                }));
    }

    getUserStationData(businessId: BusinessId): Observable<Array<IStationStatsV1>> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'text' as 'json'
        };
        const stationsUrl = `${this.m_user.webApiBaseUrl}/WebService/getStatus.ashx?user=${this.m_user.email}&password=${Lib.Base64().encode(this.m_user.passwordNew)}&base64=1&callback=?`;
        return this._http.get<IStationStatsV1[]>(stationsUrl, httpOptions).pipe(
            retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
            map((playerData64: any) => {
                const stations: IStationStatsV1[] = [];
                // const startTime = performance.now();
                let stationData = Lib.Base64().decode(playerData64.replace(/\?\({ "ret": "/, '').replace(/" }\)/, ''));
                const regex: any = /&/ig;
                stationData = stationData.replaceAll(regex, '');
                this.storeSelector.logger({message: `loaded remote screens`, severity: 0});
                xml2js.parseString(stationData, (err, i_stationData) => {
                    if (err) {
                        this.storeSelector.sendEmail(ADMINISTRATOR, 'error in xml2js.parseString stations json', this?.m_user?.email, true);
                    }
                    // const endTime = performance.now();
                    // console.log(`Call to doSomething took ${endTime - startTime} milliseconds`);
                    if (i_stationData && i_stationData.Stations && i_stationData.Stations.Station && i_stationData.Stations.Station.length > 0) {
                        i_stationData.Stations.Station.forEach((k: IStationStatsV1, v) => {
                            k.$.businessId = `${businessId}`;
                            k.$.station_business_id = concatIdBusinessId(k.$.id, businessId, 1);
                            k.$.version = '1';
                            stations.push(k);
                        });
                    }
                });
                return stations;
            })
        );
    }

    getUserStationLog(payload: IStationLogRequest): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const stationsUrl = `${this.m_user.webApiBaseUrl}/Web/log.ashx?&command=read&from=${payload.from}&user=${this.m_user.email}&pass=${Lib.Base64().encode(this.m_user.passwordNew)}&base64=1`;
        return this._http.get<IStationStatsV1[]>(stationsUrl, httpOptions).pipe(
            retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
            map((data: any) => {
                // this.storeSelector.logger({message: `loaded remote screens`, severity: 0});
                return data;
            })
        );
    }

    getUserStationClearLog(payload: IStationLogRequest): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const stationsUrl = `${this.m_user.webApiBaseUrl}/Web/log.ashx?businessId=${payload.business_id}&command=clear&user=${this.m_user.email}&pass=${Lib.Base64().encode(this.m_user.passwordNew)}&base64=1`;
        return this._http.get<IStationStatsV1[]>(stationsUrl, httpOptions).pipe(
            retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
            map((data: any) => {
                return data;
            })
        );
    }

    getUserStationStatistic(payload: any): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        let stationsUrl = `https://pop.signage.me/stationStats.ashx?command=read`;
        if (payload?.lastUpdate) {
            stationsUrl = `${stationsUrl}&lastUpdate=${payload.lastUpdate}`;
        }
        return this._http.post(stationsUrl, {user: this.m_user.email, pass: this.m_user.passwordNew}, httpOptions).pipe(
            retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
            map((data: any) => {
                return data;
            })
        );
    }

    public getPreferences(i_user: IUser, globalService: string): Observable<any> {
        const header = getCredentialsHeader(i_user);
        const url = `${globalService}/getPreferences`;
        return this._http.put<any>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem getStudioProUrlV2 ' + e);
            }),
            map((v: any) => {
                return v;
            })
        );
    }

    getAccountStatistic(): Observable<any> {
        const stationsUrl = `https://galaxy.signage.me/Web/getReport.ashx?command=createConversion&group=monthly`;
        return this._http.get(
            stationsUrl,
            {
                params: {
                    user: 'alon', // this.m_user.email
                    pass: '112233' // this.m_user.passwordNew
                }
            })
            .pipe(
                retryWithBackoff(stationsUrl, this.storeSelector, this.toast),
                catchError((e) => {
                    return of(null);
                }),
                map((data: any) => {
                    return data;
                })
            );
    }

    getSharedAccountContent(): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const stationAddUrl = `https://galaxy.signage.me/Web/getSiblingBusinesses.ashx`;
        return this._http.post(stationAddUrl,
            {
                user: this.m_user.email,
                pass: this.m_user.passwordNew
            },
            httpOptions
        ).pipe(
            retryWithBackoff(stationAddUrl, this.storeSelector, this.toast),
            catchError((e) => of(null)),
            mergeMap((servers: any[]) =>
                forkJoin(
                    Object.keys(servers).map((serverId: any) => {
                        const server = SERVERS.find((v) => compareIds(v.source_id, serverId));
                        const resources = servers[serverId];
                        const url = `https://${server.name}.signage.me/Web/getSharedContents.ashx`;
                        return this._http.post(url, resources, httpOptions).pipe(
                            retryWithBackoff(url, this.storeSelector, this.toast),
                            catchError((e) => {
                                console.error('Error getSharedAccountContent server data', e);
                                return of(null);
                            }),
                            map((data: any) => {
                                return {url, data};
                            }),
                        );
                    })
                )
            )
        );
    }

    registerStationFromCode(reqCode, sceneId, stationName): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const stationAddUrl = `https://galaxy.signage.me/Web/registerPlayer.ashx`;
        return this._http.post(stationAddUrl,
            {
                user: this.m_user.email,
                pass: this.m_user.passwordNew,
                sceneId,
                reqCode,
                stationName,
            },
            httpOptions
        ).pipe(
            retryWithBackoff(stationAddUrl, this.storeSelector, this.toast),
            catchError((e) => {
                return of(null);
            }),
            map((data: any) => {
                return data;
            })
        );
    }

    clearStationStatistic(): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const url = `https://pop.signage.me/stationStats.ashx?command=clear`;
        return this._http.post(url,
            {
                user: this.m_user.email,
                pass: this.m_user.passwordNew,
            },
            httpOptions
        ).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of(null);
            }),
            map((data: any) => {
                return data;
            })
        );
    }

    // public getUserStationDataNext(businessId: BusinessId): Observable<Array<IStationStats>> {
    //     const observables: Array<Observable<any>> = [];
    //     // tslint:disable-next-line:forin
    //     const stationsUrls = [
    //         `${this.m_user.webApiBaseUrl}/WebService/getStatus.ashx?user=${this.m_user.email}&password=${this.m_user.passwordNew}&callback=?`,
    //         `${this.m_user.webApiBaseUrl}/WebService/getStatus.ashx?user=${this.m_user.email}&password=${this.m_user.passwordNew}&callback=?`
    //     ];
    //     const httpOptions = {
    //         headers: new HttpHeaders({'Content-Type': 'application/json'}),
    //         responseType: 'text' as 'json'
    //     };
    //     // tslint:disable-next-line
    //     for (const u in stationsUrls) {
    //         const url = stationsUrls[u];
    //         observables.push(
    //             this._http.get<IStationStats[]>(url, httpOptions).pipe(
    //                 retryWithBackoff(url, this.storeSelector, this.toast),
    //                 map((playerData64: any) => {
    //                     const stations: IStationStats[] = [];
    //                     const stationData = Lib.Base64().decode(playerData64.replace(/\?\({ "ret": "/, '').replace(/" }\)/, ''));
    //                     this.storeSelector.logger({message: `loaded remote screens`, severity: 0});
    //                     xml2js.parseString(stationData, (err, i_stationData) => {
    //                         if (i_stationData && i_stationData.Stations && i_stationData.Stations.Station && i_stationData.Stations.Station.length > 0) {
    //                             i_stationData.Stations.Station.forEach((k: IStationStats, v) => {
    //                                 k.$.businessId = `${this.m_user.businessId}`;
    //                                 k.$.station_business_id = concatIdBusinessId(k.$.id, this.m_user.businessId);
    //                                 stations.push(k);
    //                             });
    //                         }
    //                     });
    //                     return stations;
    //                 })
    //             )
    //         );
    //     }
    //     return forkJoin(observables).pipe(
    //         map((val) => {
    //             return val;
    //         }));
    // }


    verifySocialToken(i_socialUser: SocialUser): Observable<ISocialServerReply> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        let socialUrl = '';
        if (i_socialUser.provider === 'AMAZON') {
            console.log('LOG: AMAZON');
            socialUrl = '';
        } else if (i_socialUser.provider === 'LDAP') {
            console.log('LOG: LDAP');
            const token = jsonToBase64(i_socialUser);
            socialUrl = `/ldapAuthVerify/${token}`;
        } else if (i_socialUser.provider === 'GOOGLE') {
            console.log('LOG: GOOGLE');
            socialUrl = `/googleAuth/${i_socialUser.idToken}`;
        } else if (i_socialUser.provider === 'FACEBOOK') {
            console.log('LOG: FACEBOOK');
            socialUrl = `/auth/facebook/token?access_token=${i_socialUser.authToken}`;
        } else {
            alert('error: problem detecting login type 1');
        }
        if (i_socialUser.provider === 'AMAZON') {
            console.log('LOG: AMAZON');
            const amazonAuthUser: ISocialServerReply = {
                socialUser: i_socialUser,
                profile: {
                    access: {
                        user: i_socialUser.firstName,
                        name: i_socialUser.name,
                        company: ''
                    }
                },
                isReseller: null,
                msUser: null,
                msPassword: null,
                exists: null
            };
            return of(amazonAuthUser);
        }

        // if (i_socialUser.provider === 'GOOGLE') {
        //     const googleAuthUser: ISocialServerReply = {
        //         socialUser: i_socialUser,
        //         profile: {
        //             access: {
        //                 user: i_socialUser.firstName,
        //                 name: i_socialUser.name,
        //                 company: ''
        //             }
        //         },
        //         isReseller: null,
        //         msUser: null,
        //         msPassword: null,
        //         exists: null
        //     };
        //     return of(googleAuthUser);
        // }

        /** facebook google and ldap now require token auth **/
        const url = `${this.appSettings.globalService}${socialUrl}`;
        return this._http.get<ISocialServerReply>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ISocialServerReply) => {
                return v;
            })
        );
    }


    //// we only need this section if we bring back email verification..........................
    //  accountStatus(businessId): Observable<IAccountReply> {
    //   const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=GetAccountStatus&businessId=${businessId}`;
    //   return this._http.jsonp<any[]>(url, 'callback').pipe(
    //     retryWithBackoff(),
    //     map(accountStatus => {
    //       return {
    //         status: {
    //           businessId: String(businessId),
    //           accountStatus
    //         }
    //       };
    //     })
    //   );
    // }

    /**
     * this replaces getBusinessUserInfoV2 as it has a bug that returns -1 for payer_id sometimes,
     * even if exists instead we use our own getBusinessUserInfoV3
     */
    getBusinessUserInfoV3(i_user, i_pass, i_billingToken = null): Observable<IUserSync> {
        const wait = Lib.DevMode() ? 500 : 1500;
        const master = `${this.appSettings.protocol}${this.appSettings.masterServer}`;
        const url = `${this.appSettings.globalService}/dwBusinessUserInfoV3?masterUrl=${master}`;
        let plusResellerId = null;
        if (this.m_user) {
            if (Lib.GetAccountVersion(this.m_user) === "PLUS") {
                plusResellerId = this.m_user.resellerId;
            }
        }
        const body = {
            user: i_user,
            pass: i_pass,
            billingToken: i_billingToken,
            plusResellerId
        };
        return this._http.post<IAccountReplyV2>(url, body, httpOptionsJson).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IUserSync) => {
                if (v.status === 'fail') return v;
                const privilegeInfoXml = Lib.Base64().decode(v.accountReplyV2.businessUserInfo.privilegeInfo);
                // const customStudio = Lib.Base64().decode(v.businessUserInfo.resellerInfo.customStudio);
                // const privilegeId = v.businessUserInfo.privilegeId;
                // const accessMask = v.businessUserInfo.accessMask;
                if (v.accountReplyV2.businessUserInfo.resellerInfo && v.accountReplyV2.businessUserInfo.resellerInfo.customStudio) {
                    const playerData = Lib.Base64().decode(v.accountReplyV2.businessUserInfo.resellerInfo.customStudio);
                    xml2js.parseString(playerData, (err, resellerXml) => {
                        if (err) {
                            console.error('problem decoding enterprise studio xml');
                            this.storeSelector.sendEmail(ADMINISTRATOR, 'error in business info json', this?.m_user?.email, true);
                            this.toast.error(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                            return throwError(err + ' problem decoding enterprise studio xml');
                        }
                        v.accountReplyV2.businessUserInfo.resellerInfo.customStudio = resellerXml;
                        if (privilegeInfoXml) {
                            xml2js.parseString(privilegeInfoXml, (err2, privilegeInfo) => {
                                if (err2) {
                                    console.error('problem decoding enterprise studio privilegeInfo xml');
                                    this.storeSelector.sendEmail(ADMINISTRATOR, 'error in business info json', this?.m_user?.email, true);
                                    this.toast.error(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                                    return throwError(err + ' problem decoding enterprise studio privilegeInfo xml');
                                }
                                v.accountReplyV2.businessUserInfo.privilegeInfo = privilegeInfo;
                            });
                        }
                    });
                }
                return v;
            })
        );
    }

    public customUrl(url, data): Observable<any> {
        const header = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        return this._http.put<any>(url, data, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem customUrlPut put ' + e);
            }),
            map((v: any) => v)
        );
    }

    public createSocialServiceProfile(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/createSocialProfile`;
        return this._http.put<any>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem createSocialServiceProfile post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public twoFactorGen(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/twoFactorGen`;
        return this._http.put<any>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem twoFactorGen post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public twoFactorAuth(value): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/twoFactorAuth`;
        return this._http.put<any>(url, value.payload, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem twoFactorAuth post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public postSocialServiceProfile(
        val: {
            socialKey: ISocialToken,
            platforms: Array<any>;
            previewUrl: string;
            resourceUrl: string;
            text: string
        } & Action<string>): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/postSocial/${val.socialKey.refId}`;
        return this._http.put<any>(url, {text: val.text, platforms: val.platforms, previewUrl: val.previewUrl, resourceUrl: val.resourceUrl}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem postSocialServiceProfile post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public createSocialCreateURL(keys: ISocialToken): Observable<any> {
        console.log('createSocialCreateURL ' + JSON.stringify(keys));
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/createSocialCreateURL/${keys.refId}/${keys.profileKey}`;
        return this._http.put<any>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem createSocialCreateURL post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public loadSocialRemoveKey(val): Observable<ISocialReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/removeSocialProfile/${val.socialKey}`;
        return this._http.put<any>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem loadSocialRemoveKey post ' + e);
            }),
            map((v: ISocialReply) => {
                v.paramId = val.paramId;
                return v;
            })
        );
    }

    public loadSocialRemoveKeyWithBusinessId(): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/removeSocialProfileWithBusinessId`;
        return this._http.put<any>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem loadSocialRemoveKey post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwSignSetGlobalConfig(localStorageConfig): Observable<any> {
        if (isNullOrUndefOrEmpty(this.m_user))
            return EMPTY;
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSignSetGlobalConfig`;
        return this._http.put<any>(url, localStorageConfig, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem dwSignSetGlobalConfig post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwDallImage(v): Observable<any> {
        const header = getCredentialsHeaderImage(this.m_user);
        const url = `${this.appSettings.globalService}/dwDallGetImage`;
        return this._http.put<any>(url, {url: v.prompt}, header).pipe(
            catchError((e) => {
                return of('problem dwDallImage post ' + e);
            }),
            map((val) => val)
        );
    }

    public dwAIImages(prompt): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwDallImageV2`;
        return this._http.put<any>(url, prompt, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem dwDallImages post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwVeoVideo(i_prompt: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwVeoVideo`;
        const inDevMode = isHostLocal();
        // const inDevMode = false;
        return this._http.put<any>(url, {message: i_prompt, dev: inDevMode}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem dwDallImages post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwSignGetGlobalConfig(localStorageConfig): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSignGetGlobalConfig`;
        return this._http.put<any>(url, localStorageConfig, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                return of('problem dwSignGetGlobalConfig post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwChatStats(orderId): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwChatStats`;
        return this._http.put<any>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwDeleteOrder post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwSignStudioStats(orderId): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSignStudioStats`;
        return this._http.put<any>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwDeleteOrder post ' + e);
            }),
            map((v: any) => v)
        );
    }


    dwPreloginGetResellerInfo(resellerId): Observable<IPreloadResellerInfo> {
        const url = `${this.appSettings.globalService}/dwPreloginGetResellerInfo/${resellerId}`;
        return this._http.get<any>(url, httpOptionsJson).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((preloadResellerInfo: IPreloadResellerInfo) => {
                if (!preloadResellerInfo?.custom_studio) return null;
                xml2js.parseString(preloadResellerInfo.custom_studio, (err, jsonCustomStudio) => {
                    if (err) return console.log('problem parsing prelogin studio XML');
                    preloadResellerInfo.custom_studio_json = jsonCustomStudio?.Studio;
                });
                return preloadResellerInfo;
            })
        );
    }

    /** previous version which did not create / create payer id, so until we released we will use getBusinessUserInfoV3, later Alon needs to add payer id creat **/
    // getBusinessUserInfoV2(i_user, i_pass): Observable<IAccountReplyV2> {
    //     const wait = Lib.DevMode() ? 500 : 1500;
    //     const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=GetBusinessUserInfo&userName=${i_user}&password=${i_pass}&ver=2`;
    //     return this._http.jsonp<any>(url, 'callback').pipe(
    //         retryWithBackoff(url, this.storeSelector, this.toast),
    //         delay(wait),
    //         map((v: IAccountReplyV2) => {
    //             if (v.result) {
    //                 return v;
    //             } else {
    //                 const privilegeInfoXml = Lib.Base64().decode(v.businessUserInfo.privilegeInfo);
    //                 // const customStudio = Lib.Base64().decode(v.businessUserInfo.resellerInfo.customStudio);
    //                 // const privilegeId = v.businessUserInfo.privilegeId;
    //                 // const accessMask = v.businessUserInfo.accessMask;
    //                 if (v.businessUserInfo.resellerInfo && v.businessUserInfo.resellerInfo.customStudio) {
    //                     const playerData = Lib.Base64().decode(v.businessUserInfo.resellerInfo.customStudio);
    //                     xml2js.parseString(playerData, (err, resellerXml) => {
    //                         if (err) {
    //                             console.error('problem decoding enterprise studio xml');
    //                             this.toast.error(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
    //                             this.storeSelector.sendEmail(ADMINISTRATOR, 'error in priv json', this?.m_user?.email, true);
    //                             return throwError(err + ' problem decoding enterprise studio xml');
    //                         }
    //                         v.businessUserInfo.resellerInfo.customStudio = resellerXml;
    //                         if (privilegeInfoXml) {
    //                             xml2js.parseString(privilegeInfoXml, (err2, privilegeInfo) => {
    //                                 if (err2) {
    //                                     console.error('problem decoding enterprise studio privilegeInfo xml');
    //                                     this.storeSelector.sendEmail(ADMINISTRATOR, 'error in priv json', this?.m_user?.email, true);
    //                                     this.toast.error(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
    //                                     return throwError(err + ' problem decoding enterprise studio privilegeInfo xml');
    //                                 }
    //                                 v.businessUserInfo.privilegeInfo = privilegeInfo;
    //                             });
    //                         }
    //
    //                     });
    //                 }
    //                 return v;
    //             }
    //         })
    //     );
    // }

    // createAccountV3(options: IAccountRequest): Observable<any> {
    //     const url = `${this.appSettings.globalService}/createAccountV3`;
    //     const header = {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'json' as 'json'};
    //     return this._http.put<IAccountReplyV2>(url, options, header).pipe(
    //         retryWithBackoff(url, this.storeSelector, this.toast),
    //         catchError((e) => {
    //             this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
    //             return of('problem saveCustomerDynawebsInfo post ' + e);
    //         }),
    //         map((v: any) => v)
    //     );
    // }

    dwLdapAuth(options: IAccountRequest): Observable<IAccountReplyV2> {
        const header = {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'json' as 'json'};
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.globalService}/ldapAuth`;
        return this._http.put<IAccountRequest>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IAccountReplyV2) => {
                return v;
            })
        );
    }

    /* version 1 Kayako */
    liveChatStatus(): Observable<any> {
        const header = {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'json' as 'json'};
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.globalService}/dwGetLiveChat`;
        return this._http.get<any>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: any) => {
                return v.liveChatStatus;
            })
        );
    }

    /* version 2 DynaWebs */
    liveChatStatusV2(): Observable<any> {
        const header = {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'json' as 'json'};
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.globalService}/chatSupport/1`;
        return this._http.get<any>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IChatServerReply) => {
                return v;
            })
        );
    }

    createAccount(accountRequest: IAccountRequest): Observable<IAccountReplyV2> {
        /**
         // enable this line if in dev mode wish to create a fake account sherwin3@ms.com ...
         const v: IAccountReplyV2 = {result: 'pass', businessId: '391067'};
         return new Observable(subscriber => {
         setTimeout(() => {
         subscriber.next(v);
         subscriber.complete();
         }, 3000);
         });
         **/
        const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=CreateCustomerAccount&businessName=${accountRequest.createCustomerAccount.businessName}&userName=${accountRequest.createCustomerAccount.userName}&password=${Lib.Base64().encode(accountRequest.createCustomerAccount.password)}&templateBusinessId=${accountRequest.createCustomerAccount.templateBusinessId}&resellerId=${accountRequest.createCustomerAccount.resellerId}&firstName=${accountRequest.createCustomerAccount.firstName}&lastName=${accountRequest.createCustomerAccount.lastName}&contactEmail=${accountRequest.createCustomerAccount.contactEmail}&workPhone=${accountRequest.createCustomerAccount.workPhone}&cellPhone=${accountRequest.createCustomerAccount.cellPhone}&address=${accountRequest.createCustomerAccount.address}&city=${accountRequest.createCustomerAccount.city}&state=${accountRequest.createCustomerAccount.state}&contry=${accountRequest.createCustomerAccount.contry}&zipcode=${accountRequest.createCustomerAccount.zipcode}&studioType=${accountRequest.createCustomerAccount.studioType}&&emailUpdates=${accountRequest.createCustomerAccount.emailUpdates}&base64=1`;
        return this._http.jsonp<any[]>(url, 'callback').pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IAccountReplyV2) => {
                return v;
            })
        );
    }

    getAnnualPayData(payload): Observable<IAnnualPayData> {
        const url = `https://pay.signage.me/getPaymentInfo.ashx?args=${payload.paymentId}`;
        return this._http.post(url, {}).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IAnnualPayData) => {
                return v;
            })
        );
    }

    updateAnnualPayData(v: any): Observable<IAnnualPayData> {
        const url = `https://pay.signage.me/payPendingPayment.ashx?args=${v.paymentId}`;
        return this._http.post(url, v.payload).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IAnnualPayData) => {
                return v;
            })
        );
    }


    resetPassword(accountRequest: IAccountRequest): Observable<IAccountReplyV2> {
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=ResetPassword&userName=${accountRequest.createCustomerAccount.userName}`;
        return this._http.jsonp<any[]>(url, 'callback').pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IAccountReplyV2) => {
                return v;
            })
        );
    }

    changePassword(accountRequest: IAccountRequest): Observable<IAccountReplyV2> {
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=ChangePassword&userName=${accountRequest.createCustomerAccount.userName}&oldPassword=${Lib.Base64().encode(accountRequest.createCustomerAccount.password)}&newPassword=${Lib.Base64().encode(accountRequest.createCustomerAccount.newPass)}&base64=1`;
        return this._http.jsonp<any[]>(url, 'callback').pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IAccountReplyV2) => {
                return v;
            })
        );
    }

    changeBusinessName(accountRequest: IAccountRequest): Observable<IAccountReplyV2> {
        const wait = Lib.DevMode() ? 500 : 1500;
        const url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/createNewAccount.ashx?command=ChangeBusinessName&userName=${accountRequest.createCustomerAccount.userName}&password=${Lib.Base64().encode(accountRequest.createCustomerAccount.password)}&newBusnessName=${accountRequest.createCustomerAccount.businessName}&base64=1`;
        return this._http.jsonp<any[]>(url, 'callback').pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            delay(wait),
            map((v: IAccountReplyV2) => {
                return v;
            })
        );
    }

    sendEmail(options: IEmailSend): Observable<IEmailSend> {
        const header = {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'text' as 'json'};
        const url = `${this.appSettings.globalService}/sendEmailStudio`;
        return this._http.put<IEmailSend>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IEmailSend) => v)
        );
    }

    ldapAuth(options): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'text' as 'json'
        };
        const url = `${this.appSettings.globalService}/sendEmailStudio`;
        return this._http.put<any>(url, options, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: any) => v)
        );
    }

    getAccountDetails(): Observable<IAccountDetails> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'text' as 'json'
        };
        const url = `${this.m_user.webApiResellerBaseUrl}&command=GetAccountInfo`;
        let enterpriseReply;
        return this._http.get<IAccountDetails>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map(v => {
                xml2js.parseString(v, (err, userAccountDetails) => {
                    if (err) {
                        console.error('getUserAccountDetails xml conversion error');
                        this.storeSelector.sendEmail(ADMINISTRATOR, 'error in account details json', this?.m_user?.email, true);
                        this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                    }
                    // enterpriseReply = Object.assign(userAccountDetails, accountDetailsTemplate);
                    // let c = _.extend(userAccountDetails, accountDetailsTemplate);
                    // console.log(c);
                    enterpriseReply = userAccountDetails;
                });
                return enterpriseReply;
            })
        );
    }

    getAccountDetailsBillingToken(v): Observable<IAccountDetails> {
        const textXml = {
            headers: new HttpHeaders({
                'Content-Type': 'text/xml',
                'Accept': 'text/xml'
            }), responseType: 'text' as 'json'
        };
        const url = `${this.appSettings.globalService}/getAccountInfo/${v.password}`;
        let enterpriseReply;
        return this._http.put<IOrderForm[]>(url, {}, textXml).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map(v => {
                xml2js.parseString(v, (err, userAccountDetails) => {
                    if (err) {
                        console.error('getUserAccountDetails xml conversion error');
                        this.storeSelector.sendEmail(ADMINISTRATOR, 'error in account details json', this?.m_user?.email, true);
                        this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                    }
                    enterpriseReply = userAccountDetails;
                });
                return enterpriseReply;
            })
        );
    }

    getOrders(): Observable<IOrderForm[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/orders_v2/${this.m_user.enterprise ? 'ENTERPRISE' : 'USER'}`;
        return this._http.put<IOrderForm[]>(url, {accountVersion: Lib.GetAccountVersion(this.m_user)}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IOrderForm[]) => v)
        );

        // const url = `${this.appSettings.globalService}/orders/${Lib.EncryptValue(this.m_user.username)}/${Lib.EncryptValue(this.m_user.passwordNew)}/${this.m_user.enterprise ? 'ENTERPRISE' : 'USER'}/1`;
        // const url = `${this.appSettings.globalService}/orders/${this.m_user.username}/${this.m_user.passwordNew}/${this.m_user.enterprise ? 'ENTERPRISE' : 'USER'}`;
        // return this._http.get<IOrderForm[]>(url, httpOptionsJson).pipe(
        //     retryWithBackoff(url, this.storeSelector, this.toast),
        //     map((v: IOrderForm[]) => v)
        // );
    }

    public getOrder(orderId: Id): Observable<IOrderDetails> {
        // const orderId = order.order.order_id ? order.order.order_id : order.billing.payer_id;
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/order_v2/${orderId}/${this.m_user.enterprise ? 'ENTERPRISE' : 'USER'}`;

        const obs1$ = this._http.put(url, {accountVersion: Lib.GetAccountVersion(this.m_user)}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IOrderDetails) => v)
        );

        const obs2$ = this.fintechService.getOrderInfo(orderId);


        return combineLatest(obs1$, obs2$).pipe(
            map(([value1, value2]) => {
                if (value2.status == "succeeded") {
                    value1.billing.company = value2.contact_company;
                    value1.billing.vat = value2.contact_vat;
                    value1.billing.first_name = value2.contact_first_name;
                    value1.billing.last_name = value2.contact_last_name;
                    value1.billing.phone1 = value2.contact_phone1;
                    value1.billing.phone2 = value2.contact_phone2;
                    value1.billing.email = value2.contact_email;

                    value1.shipping.company = value2.shipping_company;
                    value1.shipping.first_name = value2.shipping_first_name;
                    value1.shipping.last_name = value2.shipping_last_name;
                    value1.shipping.address1 = value2.shipping_address1;
                    value1.shipping.address2 = value2.shipping_address2;
                    value1.shipping.city = value2.shipping_city;
                    value1.shipping.state = value2.shipping_state;
                    value1.shipping.country = value2.shipping_country;
                    value1.shipping.zip_code = value2.shipping_zip_code;
                    value1.shipping.phone1 = value2.shipping_phone1;
                    value1.shipping.phone2 = value2.shipping_phone2;
                    value1.shipping.email = value2.shipping_email;

                    value1.billing.card_number = value2.card_last4;
                    value1.billing.card_brand = value2.card_brand;
                    value1.billing.expiration_month = value2.card_expiration_month;
                    value1.billing.expiration_year = value2.card_expiration_year;
                    value1.billing.cardholder = value2.card_cardholder;
                    value1.billing.address1 = value2.card_address1;
                    value1.billing.address2 = value2.card_address2;
                    value1.billing.city = value2.card_city;
                    value1.billing.state = value2.card_state;
                    value1.billing.zip_code = value2.card_postal_code;
                    value1.billing.country = value2.card_country;
                }
                return value1;
            })
        );


        // const url = `${this.appSettings.globalService}/order/${this.m_user.username}/${this.m_user.passwordNew}/${orderId}/${this.m_user.enterprise ? 'ENTERPRISE' : 'USER'}`;
        // return this._http.get<IOrderDetails>(url, httpOptionsJson).pipe(
        //     retryWithBackoff(url, this.storeSelector, this.toast),
        //     map((v: IOrderDetails) => v)
        // );
        //
        // var appdb: Map<string, any> = this.appStore.getState().appdb;
        // var url;
        // url = appdb.get('appBaseUrlCloud').replace('END_POINT', 'order') + `/${orderId}/${accountType}`;
        // this._http.get(url)
        //     .catch((err) => {
        //         bootbox.alert('Error getting order details');
        //         return Observable.throw(err);
        //     })
        //     .finally(() => {
        //     })
        //     .map((result: any) => {
        //         var order: any = result.json();
        //         var orderDetailModel: OrderDetailModel = new OrderDetailModel(order);
        //         dispatch(this.receivedOrder(orderDetailModel));
        //     }).subscribe();
    }

    // getAccountStaticTemplates(): Observable<Array<ISceneTemplateImport>> {
    //   let url;
    //   const templateData = staticAccountTemplates;
    //   this.appSettings.workOffline ? url = this.appSettings.apiOfflineUrl + 'samples.json' : url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/getResellerTemplates.ashx`;
    //   const sceneTemplates: ISceneTemplateImport[] = [];
    //   // tslint:disable-next-line
    //   for (let templateItem in templateData) {
    //     const item = templateData[templateItem];
    //     const sampleItem: ISceneTemplateImport = {
    //       studioLite: '1',
    //       templateBusinessId: item[0],
    //       name: item[3].name,
    //       video: null, // `${this.appSettings.cloudResources}/business1000/resources/samples/${templateBusinessId}.mp4`,
    //       icon: [
    //         `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/sign_html/${item[0]}_1.png`,
    //         `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/sign_html/${item[0]}_2.png`,
    //         `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/sign_html/${item[0]}_3.png`,
    //         `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/sign_html/${item[0]}_4.png`
    //       ],
    //       category: item[3].folder,
    //       preview: null
    //     };
    //     sceneTemplates.push(sampleItem);
    //   }
    //   return of(sceneTemplates);
    // }

    getTemplatesFromServer(): Observable<Array<ISceneTemplateImport>> {
        let url;
        this.appSettings.workOffline ? url = this.appSettings.apiOfflineUrl + 'samples.json' : url = `${this.appSettings.protocol}${this.appSettings.masterServer}/WebService/getResellerTemplates.ashx`;
        return (this.appSettings.workOffline ? this._http.get<any[]>(url) : this._http.jsonp<any[]>(url, 'callback')).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((templateData: any) => {
                // tslint:disable-next-line:no-shadowed-variable
                const sceneTemplates: ISceneTemplateImport[] = [];
                // let totalLiteTemplates = 0;
                // tslint:disable-next-line
                for (let templateName in templateData.templates) {
                    // const isFullFeatures = true;
                    // if (isFullFeatures && template.lite === '1') {
                    //   totalLiteTemplates++;
                    //   template.businessId = '575579';
                    //   template.name = 'SignStudio template';
                    //   if (totalLiteTemplates > 1) continue;
                    // }
                    let newName;
                    let isTouch = false;
                    const template = templateData.templates[templateName];

                    if (template.name.match('simple')) {
                        newName = template.name.replace(/_simple/, '');
                        template.name = newName + ' (simple)';
                    } else if (template.name.match('touch')) {
                        newName = template.name.replace(/_touch/, '');
                        template.name = newName + ' (touch)';
                        isTouch = true;
                    }

                    // if (i_templateStyle !== SceneTemplateEnum.ALL && template.lite !== i_templateStyle) continue;
                    const templateBusinessId = template.businessId;
                    const sampleItem: ISceneTemplateImport = {
                        studioType: template.lite,
                        templateBusinessId,
                        name: template.name,
                        video: `${this.appSettings.cloudResourcesGPU}/business1000/resources/samples/${templateBusinessId}.mp4`,
                        icon: `${this.appSettings.protocol}${this.appSettings.masterServer}/Resources/Images/lite_html/${templateBusinessId}.png`,
                        preview: template.previewUrl,
                        touch: isTouch
                    };
                    sceneTemplates.push(sampleItem);
                }
                // tslint:disable-next-line
                for (let templateName in staticAccountTemplates) {
                    const item = staticAccountTemplates[templateName];
                    const templateBusinessId = item[0];
                    const isTouch = templateName.includes('Touch');

                    const sampleItem: ISceneTemplateImport = {
                        studioType: EnumStudioType.SIGN_STUDIO,
                        templateBusinessId: item[0],
                        name: item[3].name + (isTouch ? ' (touch)' : ''),
                        video: item[3]?.video ? item[3].video : null, // `${this.appSettings.cloudResources}/business1000/resources/samples/${templateBusinessId}.mp4`,
                        icon: `assets/images/accountIcons/${templateBusinessId}.png`,
                        snaps: this.getSignStudioTemplateLargeSnapshots(templateBusinessId),
                        category: item[3].folder,
                        preview: null,
                        touch: isTouch
                    };
                    sceneTemplates.push(sampleItem);
                }
                return sceneTemplates;
            })
        );
    }

    private getSignStudioTemplateLargeSnapshots(templateBusinessId): Array<string> {
        const templateSnaps = [];
        Object.keys(sceneTemplates).forEach((templateName) => {
            const template: ISceneImportTemplate = {};
            template[templateName] = sceneTemplates[templateName];
            const businessId = template[templateName][0];
            if (businessId === templateBusinessId) {
                templateSnaps.push(`${appData.cloudResourcesS3}/business1000/resources/scenes_snaps/${templateName}.png`);
            }
        });
        return templateSnaps;
    }

    public sendStationCommand(i_command, i_selectionModel: SelectionModel<IStationData>, i_filterBySceneId: string = null) {
        this.cod(
            from(i_selectionModel.selected)
                .pipe(
                    take(i_selectionModel.selected.length),
                    map((i_station) => {
                        if (i_filterBySceneId && !compareIds(i_filterBySceneId, i_station.props.player_data_id))
                            return;
                        const stationId = i_station.props.station_id;
                        this.storeSelector.getStation(i_station)
                            .pipe(switchMap((i_station2) => {
                                if (i_station2?.version === '2') {
                                    const segment = 'Web';
                                    const url = `${this.coreAppData[1].protocol}${this.coreAppData[0].businessDomain}/${segment}/sendCommand.ashx?user=${this.coreAppData[0].email}&pass=${Lib.Base64().encode(this.coreAppData[0].passwordNew)}&stationId=${stationId}&base64=1`;
                                    Lib.DevShow(url);
                                    return this._http.post(url, {
                                        command: i_command
                                    }).pipe(
                                        retryWithBackoff(url, this.storeSelector, this.toast),
                                        delay(_.random(1, 1000)),
                                        map((v) => v)
                                    );
                                } else {
                                    const segment = 'WebService';
                                    const url = `${this.coreAppData[1].protocol}${this.coreAppData[0].businessDomain}/${segment}/sendCommand.ashx?i_user=${this.coreAppData[0].email}&i_password=${Lib.Base64().encode(this.coreAppData[0].passwordNew)}&i_stationId=${stationId}&i_command=${i_command}&i_param1='SignageStudioLite'&i_param2=''&base64=1`;
                                    Lib.DevShow(url);
                                    return this._http.jsonp<any>(url, 'callback').pipe(
                                        retryWithBackoff(url, this.storeSelector, this.toast),
                                        delay(_.random(1, 1000)),
                                        map((v) => v)
                                    );
                                }
                            }))
                            .subscribe((res: any) => {
                                Lib.DevShow(`send command response ${res?.ret}`);
                            });
                    }),
                ).subscribe((v) => {
            })
        );
    }

    /**
     * Dynawebs
     */

    public dwGetBusinessUsage(cartRequest: any): Observable<IBusinessUsage> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwBusinessUsage/${cartRequest.businessId}`;
        return this._http.get<IBusinessUsage>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IBusinessUsage) => {
                return v;
            })
        );
    }

    public dwCartGetOrderId(options: ICartRequest): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCartGetOrderId`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwCartGetOrderId ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwTextToSpeechCreate(text: string): Observable<any> {
        // disable credentials test for Oscar to use as open public service
        // const header = getCredentialsHeader(this.m_user);
        const header = {};
        const url = `${this.appSettings.globalService}/dwTextToSpeechCreate`;
        return this._http.put<any>(url, text, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem saveCustomerDynawebsInfo post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwTextToSpeechUpload(text: string): Observable<any> {
        // disable credentials test for Oscar to use as open public service
        // const header = getCredentialsHeader(this.m_user);
        const header = {};
        const url = `${this.appSettings.globalService}/dwTextToSpeechUpload`;
        return this._http.put<any>(url, text, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem saveCustomerDynawebsInfo post ' + e);
            }),
            map((v: any) => v)
        );
    }


    public saveCustomerDynawebsInfo(options: IOrderForm): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSaveCustomerInfo`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem saveCustomerDynawebsInfo post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public saveEquipment(options: IOrderForm): Observable<IUserUpgradeReply> {
        let header: object;
        const url = `${this.appSettings.globalService}/saveEquipment`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem saveCustomerInfo post ' + e);
            }),
            map((v: any) => {
                return v;
            })
        );
    }

    public loadEquipment(vehicleId: string): Observable<IUserUpgradeReply> {
        const url = `${this.appSettings.globalService}/loadEquipment/${vehicleId}`;
        return this._http.get<IOrderForm>(url).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem saveCustomerInfo post ' + e);
            }),
            map((v: any) => {
                return v;
            })
        );
    }


    cartDynawebsAddContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCartAddContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            })
        );
    }

    getDynawebsNewOrder(payerId): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCreateOrder/${payerId}`;
        return this._http.get<ICartReply[]>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => v)
        );
    }

    searchDynawebsServers(options): Observable<IDynawebsServerReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSearchServer`;
        return this._http.put<IDynawebsServerReply>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IDynawebsServerReply) => v)
        );
    }

    saveDynawebsServer(options): Observable<IDynawebsServerReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwSaveServer`;
        return this._http.put<IDynawebsServerReply>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IDynawebsServerReply) => v)
        );
    }

    orderStatusDynawebs(status): Observable<IOrderForm[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwOrderStatus/${status}`;
        return this._http.put<any>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: IOrderForm[]) => v)
        );
    }

    getDynawebsProducts(): Observable<Array<ICartProduct>> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const url = `${this.appSettings.globalService}/dwCartGetProducts`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: Array<ICartProduct>) => v)
        );
    }

    cartRemoveDynawebsContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCartRemoveContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            })
        );
    }


    public upgradeAccount(options: object): Observable<IUserSaveDetailsReply> {
        options["customerUser"] = this.m_user.email;
        options["customerPass"] = this.m_user.passwordNew;
        options["version"] = "2.0";


        const url = "https://galaxy.signage.me/Web/upgradeToResellerAccount.ashx";
        this.toast.info(this.translocoService.translate('toast.pleaseWaitUpgradingAccount', {}, this.translocoService.getActiveLang()));
        return this._http.post(url, options).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem upgradeEnterprise post ' + e);
            }),
            map((v: any) => {
                return v;
            })
        );
    }

    public dynawebsUpgradeAccount(options: object): Observable<IUserSaveDetailsReply> {
        options["billingUser"] = this.m_user.email;
        options["billingPass"] = this.m_user.passwordNew;
        options["dynawebs"] = true;
        options["command"] = "upgradeToResellerAccount";
        const url = "https://galaxy.signage.me/Web/upgradeToResellerAccount.ashx";
        return this._http.post(url, options)
            .pipe(
                map((v: any) => v)
            );
    }

    public getBillingService(options: object): Observable<IUserSaveDetailsReply> {
        options["billingUser"] = this.m_user.email;
        options["billingPass"] = this.m_user.passwordNew;
        const url = "https://galaxy.signage.me/Web/billingService.ashx";
        return this._http.post(url, options)
            .pipe(
                map((v: any) => v)
            );
    }

    public getStripeService(options: object): Observable<IUserSaveDetailsReply> {
        options["customerUser"] = this.m_user.email;
        options["customerPass"] = this.m_user.passwordNew;
        const url = "https://galaxy.signage.me/Web/stripeService.ashx";
        return this._http.post(url, options)
            .pipe(
                map((v: any) => v)
            );
    }

    public dwLoadCustomer(options): Observable<IOrderForm> {
        // alon
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwLoadCustomer/${options.dwLoad}/${options.orderId}`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwLoadCustomer post ' + e);
            }),
            switchMap(v => {
                const obs2$ = this.fintechService.getOrderInfo((<any> v).order_id);
                return combineLatest(of(v), obs2$).pipe(
                    map(([value1, value2]: [IOrderForm, any]) => {
                        if (value2.status == "succeeded") {
                            value1.mail = value2.contact_email;

                            value1.tel = value2.contact_phone1;

                            value1.billingCompany = value2.contact_company;
                            value1.billingFirstName = value2.contact_first_name;
                            value1.billingLastName = value2.contact_last_name;
                            value1.shippingCompany = value2.shipping_company;
                            value1.shippingFirstName = value2.shipping_first_name;
                            value1.shippingLastName = value2.shipping_last_name;
                            value1.shippingAddress1 = value2.shipping_address1;
                            value1.shippingAddress2 = value2.shipping_address2;
                            value1.shippingCity = value2.shipping_city;
                            value1.shippingState = value2.shipping_state;
                            value1.shippingCountry = value2.shipping_country;
                            value1.shippingZip = value2.shipping_zip_code;
                            value1.phone1 = value2.shipping_phone1;
                            value1.phone2 = value2.shipping_phone2;
                            value1.card_number = value2.card_last4;
                            value1.expiration_month = value2.card_expiration_month;
                            value1.expiration_year = value2.card_expiration_year;
                            value1.billingAddress1 = value2.card_address1;
                            value1.billingAddress2 = value2.card_address2;
                            value1.billingCity = value2.card_city;
                            value1.billingState = value2.card_state;
                            value1.billingZip = value2.card_postal_code;
                            value1.billingCountry = value2.card_country;
                        }

                        return value1;
                    }),
                    map((v: any) => {
                        if (v?.error) alert('err: ' + v.error);
                        return v;
                    })
                );
            })
        );
    }

    public dwDeleteOrder(orderId): Observable<IOrderForm> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwDeleteOrder/${orderId}`;
        return this._http.put<IOrderForm>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwDeleteOrder post ' + e);
            }),
            map((v: any) => v)
        );
    }

    // public dwSubscriberGetPayerId(payload): Observable<IOrderForm> {
    //     const httpOptions = {
    //         headers: new HttpHeaders({'Content-Type': 'application/json'}),
    //         responseType: 'json' as 'json'
    //     };
    //     const url = `${this.appSettings.globalService}/msAuthenticateToken_v2`;
    //     return this._http.put<IOrderForm>(url, {token: payload.cartRequest.payerIdToken, version: 'v3'}, httpOptions).pipe(
    //         retryWithBackoff(url, this.storeSelector, this.toast),
    //         catchError((e) => {
    //             this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
    //             return of('problem dwSubscriberGetPayerId post ' + e);
    //         }),
    //         map((v: any) => v)
    //     );
    // }


    public dwGenerateEncryptedId(payload): Observable<IOrderForm> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwGenerateEncryptedId/${payload.businessId}`;
        return this._http.put<IOrderForm>(url, '', header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwDeleteOrder post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwFindOrder(options): Observable<IOrderForm[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwFindOrder`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwFindOrder post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwFindCustomer(options): Observable<IDynawebsCustomer[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwFindCustomer`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwFindCustomer post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwRenameCustomer(options): Observable<IOrderForm[]> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwRenameUser`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwRenameCustomer post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwCheckUserExists(value): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const url = `${this.appSettings.globalService}/dwCheckUserExists/${value.user}`;
        return this._http.put<any>(url, '', httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwCheckUserExists post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwCartGetContent(options: ICartRequest): Observable<ICart> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwCartGetContent/${options.cartCreate.orderId}`;
        return this._http.put<ICart>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwCartGetContent post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwGetChatArchive(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwGetChatArchive/${val}`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwGetChatArchive post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwGetChatArchiveSearch(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwGetChatArchiveSearch/${val}`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwGetChatArchiveSearch post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwReportSubscribers(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwReportSubscribers`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwGetChatArchiveSearch post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwReportPayers(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwReportPayers`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwGetChatArchiveSearch post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwReportNonPayers(val: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/dwReportNonPayers`;
        return this._http.put<any>(url, val, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwGetChatArchiveSearch post ' + e);
            }),
            map((v: any) => v)
        );
    }

    public dwGetAccountDetails(businessId: any): Observable<any> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/getAccountStatusBusiness/${businessId}`;
        return this._http.put<any>(url, {}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem dwGetChatArchiveSearch post ' + e);
            }),
            map((v: any) => v)
        );
    }

    /**
     * Shopping Cart
     */
    public saveCustomerInfoV4(options: IOrderForm): Observable<IUserUpgradeReply> {
        let header: object;
        if (options.billingToken) {
            header = {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'json' as 'json'};
        } else {
            header = getCredentialsHeader(this.m_user);
        }
        const url = `${this.appSettings.globalService}/userSaveDetails`;
        return this._http.put<IOrderForm>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem saveCustomerInfo post ' + e);
            }),
            map((v: any) => {
                return v;
            })
        );
    }

    public updateCustomerEmail(options: any): Observable<IUserUpgradeReply> {
        const header: object = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/userUpdateEmail`;
        return this._http.put<any>(url, options, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem updateCustomerEmail post ' + e);
            }),
            map((v: any) => {
                return v;
            })
        );
    }

    getCartContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const url = `${this.appSettings.globalService}/cartGetContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            })
        );
    }

    cartAddContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const url = `${this.appSettings.globalService}/cartAddContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            })
        );
    }

    cartPaypalPaymentWaiting(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/cartPaypalPaymentWaiting`;
        return this._http.put<ICartProduct>(url, cartRequest, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            })
        );
    }

    cartPaypalPayment(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/cartPaypalPayment`;
        return this._http.put<ICartProduct>(url, cartRequest, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            })
        );
    }

    cartCreditCardPayment(cartRequest: ICartRequest): Observable<ICartReply> {
        const header = getCredentialsHeader(this.m_user);
        const url = `${this.appSettings.globalService}/cartCreditCardPayment`;
        return this._http.put<ICartProduct>(url, cartRequest, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            })
        );
    }

    cartRemoveContent(cartRequest: ICartRequest): Observable<ICartReply> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const url = `${this.appSettings.globalService}/cartRemoveContent/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartReply) => {
                return v;
            })
        );
    }

    cartUpdateShipping(cartRequest: ICartRequest): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const url = `${this.appSettings.globalService}/cartUpdateShipping/${jsonToBase64(cartRequest)}`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v) => {
                return v;
            })
        );
    }

    public postChatStats(options: IChatStats): Observable<any> {
        const url = `${this.appSettings.globalService}/chatStats`;
        return this._http.post<IChatStats>(url, options).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem chatStats post ' + e);
            }),
            map((v: any) => ({payload: {serverReplay: v}}))
        );
    }

    getCartActiveProducts(): Observable<Array<ICartProduct>> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const url = `${this.appSettings.globalService}/cartGetProducts`;
        return this._http.get<ICartProduct[]>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: Array<ICartProduct>) => {
                v = v.map((p) => {
                    if (compareIds(p.product_id, 1122)) p.inAmazon = true;
                    return p;
                });
                return v;
            })
        );
    }

    getCartCreate(): Observable<ICartCreate> {
        const httpOptions = {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
            responseType: 'json' as 'json'
        };
        const url = `${this.appSettings.globalService}/createOrder`;
        return this._http.get<ICartCreate>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((v: ICartCreate) => v)
        );
    }

    initSocketChatService(i_server) {
        this.socketChat = io(i_server, {transports: ["websocket"]});
        return this.socketChat;
    }

    private showSaveError(err, crudRecord: string) {
        console.error('showSaveError: could not complete the save operation ' + crudRecord);
        if (isGalaxy()) return;
        let message = '';
        if (err?.msg) {
            message = err.msg;
        } else if (err?.message) {
            message = err.message;
        } else if (err?.status) {
            message = err.status;
        } else {
            message = err;
        }
        this.toast.error(this.translocoService.translate('toast.sorryEncounteredProblemSave', {}, this.translocoService.getActiveLang()), message);
    }
}

